import { lazy, Suspense, type ReactNode } from "react";
import { Spin } from "antd";
import { Authenticated, Refine, usePermissions } from "@refinedev/core";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbarProvider } from "@refinedev/kbar";
import { useNotificationProvider } from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import "./styles/mobile.css";
import "./styles/theme.css";

import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { liveProvider } from "@refinedev/supabase";
import { App as AntdApp } from "antd";
import {
  DashboardOutlined,
  FunnelPlotOutlined,
  CreditCardOutlined,
  TeamOutlined,
  CalendarOutlined,
  BankOutlined,
  UsergroupAddOutlined,
  BookOutlined,
  FileOutlined,
  FileTextOutlined,
  StarOutlined,
  ExperimentOutlined,
  SettingOutlined,
  BellOutlined,
  AudioOutlined,
  RiseOutlined,
  ReadOutlined,
  UserOutlined,
  ToolOutlined,
  GiftOutlined,
  ScheduleOutlined,
  MessageOutlined,
  HistoryOutlined,
  BarChartOutlined,
  SoundOutlined,
  NotificationOutlined,
  UserDeleteOutlined,
  SafetyCertificateOutlined,
  AccountBookOutlined,
  SwapOutlined,
  WalletOutlined,
  ReconciliationOutlined,
  RetweetOutlined,
  PieChartOutlined,
  FundOutlined,
  ProjectOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  AppstoreOutlined,
  CheckSquareOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  ApiOutlined,
  BulbOutlined,
  WhatsAppOutlined,
  MailOutlined,
  FileDoneOutlined,
} from "@ant-design/icons";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { NavProvider } from "./components/app-sider/nav-context";
import { ShellLayout, LegacyKbar } from "./shell";
import { useShellV2 } from "./shell/flags";
import { findNav } from "./shell/nav";
import authProvider from "./providers/auth";
import { dataProvider } from "./providers/data";
import { supabaseClient } from "./providers/supabase-client";
import { canSeeArea, areaForPath } from "./constants";

const DashboardPage       = lazy(() => import("./pages/dashboard").then(m => ({ default: m.DashboardPage })));
const LeadsPage           = lazy(() => import("./pages/leads").then(m => ({ default: m.LeadsPage })));
const CreditsPage         = lazy(() => import("./pages/credits").then(m => ({ default: m.CreditsPage })));
const ProfilesPage        = lazy(() => import("./pages/profiles").then(m => ({ default: m.ProfilesPage })));
const AppointmentsPage    = lazy(() => import("./pages/appointments").then(m => ({ default: m.AppointmentsPage })));
const GroupsPage          = lazy(() => import("./pages/groups").then(m => ({ default: m.GroupsPage })));
const CoursesPage         = lazy(() => import("./pages/courses").then(m => ({ default: m.CoursesPage })));
const KutuphanePage       = lazy(() => import("./pages/kutuphane").then(m => ({ default: m.KutuphanePage })));
const OdevOzetPage        = lazy(() => import("./pages/odev").then(m => ({ default: m.OdevOzetPage })));
const OdevListePage       = lazy(() => import("./pages/odev/liste").then(m => ({ default: m.OdevListePage })));
const PlanlarPage         = lazy(() => import("./pages/odev/planlar").then(m => ({ default: m.PlanlarPage })));
const ReviewsPage         = lazy(() => import("./pages/reviews").then(m => ({ default: m.ReviewsPage })));
const GeriBildirimPage    = lazy(() => import("./pages/geri-bildirim").then(m => ({ default: m.GeriBildirimPage })));
const TahsilatPage        = lazy(() => import("./pages/finans/tahsilat").then(m => ({ default: m.TahsilatPage })));
const EslestirPage        = lazy(() => import("./pages/finans/eslestir").then(m => ({ default: m.EslestirPage })));
const EgitmenOdemeleriPage = lazy(() => import("./pages/finans/egitmen").then(m => ({ default: m.EgitmenOdemeleriPage })));
const KasaPage            = lazy(() => import("./pages/finans/kasa").then(m => ({ default: m.KasaPage })));
const FinansGenelPage     = lazy(() => import("./pages/finans/genel").then(m => ({ default: m.FinansGenelPage })));
const GiderPage           = lazy(() => import("./pages/finans/gider").then(m => ({ default: m.GiderPage })));
const PlacementsPage      = lazy(() => import("./pages/placements").then(m => ({ default: m.PlacementsPage })));
const SettingsPage        = lazy(() => import("./pages/ayarlar").then(m => ({ default: m.SettingsPage })));
const TeamAccessPage      = lazy(() => import("./pages/ayarlar/ekip").then(m => ({ default: m.TeamAccessPage })));
const RecordsPage         = lazy(() => import("./pages/ayarlar/kayitlar").then(m => ({ default: m.RecordsPage })));
const NotificationsPage   = lazy(() => import("./pages/notifications").then(m => ({ default: m.NotificationsPage })));
const SpeakingClubPage    = lazy(() => import("./pages/speaking-club").then(m => ({ default: m.SpeakingClubPage })));
const CoachingPage        = lazy(() => import("./pages/coaching").then(m => ({ default: m.CoachingPage })));
const CouponsPage         = lazy(() => import("./pages/coupons").then(m => ({ default: m.CouponsPage })));
const AvailabilityPage    = lazy(() => import("./pages/availability").then(m => ({ default: m.AvailabilityPage })));
const MessagesPage        = lazy(() => import("./pages/messages").then(m => ({ default: m.MessagesPage })));
const MaliyeTransactionsPage = lazy(() => import("./pages/maliye").then(m => ({ default: m.MaliyeTransactionsPage })));
const MaliyeAccountsPage     = lazy(() => import("./pages/maliye").then(m => ({ default: m.MaliyeAccountsPage })));
const MaliyePayablesPage     = lazy(() => import("./pages/maliye").then(m => ({ default: m.MaliyePayablesPage })));
const MaliyeRecurringPage    = lazy(() => import("./pages/maliye").then(m => ({ default: m.MaliyeRecurringPage })));
const MaliyeBudgetsPage      = lazy(() => import("./pages/maliye").then(m => ({ default: m.MaliyeBudgetsPage })));
const MaliyeReportsPage      = lazy(() => import("./pages/maliye").then(m => ({ default: m.MaliyeReportsPage })));
const AnnouncementsPage   = lazy(() => import("./pages/announcements").then(m => ({ default: m.AnnouncementsPage })));
const CampaignsPage       = lazy(() => import("./pages/campaigns").then(m => ({ default: m.CampaignsPage })));
const DemoPage            = lazy(() => import("./pages/demo").then(m => ({ default: m.DemoPage })));
const TeacherApplicationsPage = lazy(() => import("./pages/teacher-applications").then(m => ({ default: m.TeacherApplicationsPage })));
const LoginPage           = lazy(() => import("./pages/login").then(m => ({ default: m.LoginPage })));
const ForgotPasswordPage  = lazy(() => import("./pages/login/forgot-password").then(m => ({ default: m.ForgotPasswordPage })));
const HubPage             = lazy(() => import("./pages/hub").then(m => ({ default: m.HubPage })));
const GorevlerPage        = lazy(() => import("./pages/gorevler").then(m => ({ default: m.GorevlerPage })));
const PlanlamaBoardPage    = lazy(() => import("./pages/planlama").then(m => ({ default: m.PlanlamaBoardPage })));
const MeetingsPage        = lazy(() => import("./pages/gorevler/meetings").then(m => ({ default: m.MeetingsPage })));
const MeetingPage         = lazy(() => import("./pages/gorevler/meeting").then(m => ({ default: m.MeetingPage })));
const MeetingDetailPage   = lazy(() => import("./pages/gorevler/meeting-detail").then(m => ({ default: m.MeetingDetailPage })));
const DrivePage           = lazy(() => import("./pages/drive").then(m => ({ default: m.DrivePage })));
const DriveFavoritesPage  = lazy(() => import("./pages/drive/favorites").then(m => ({ default: m.DriveFavoritesPage })));
const DriveActivityPage   = lazy(() => import("./pages/drive/activity").then(m => ({ default: m.DriveActivityPage })));
const BuyumeInboxPage     = lazy(() => import("./pages/buyume").then(m => ({ default: m.InboxPage })));
const BuyumeChannelsPage  = lazy(() => import("./pages/buyume").then(m => ({ default: m.ChannelsPage })));
const BuyumeAiPage        = lazy(() => import("./pages/buyume").then(m => ({ default: m.AiCloserPage })));
const BuyumeAdsPage       = lazy(() => import("./pages/buyume").then(m => ({ default: m.AdsPage })));
const BuyumeContentPage   = lazy(() => import("./pages/buyume").then(m => ({ default: m.ContentStudioPage })));
const BuyumeReactivationPage = lazy(() => import("./pages/buyume").then(m => ({ default: m.ReactivationPage })));
const UiShowcasePage      = lazy(() => import("./pages/labs/ui-showcase").then(m => ({ default: m.UiShowcasePage })));
const KisilerPage         = lazy(() => import("./pages/kisiler").then(m => ({ default: m.KisilerPage })));
const BugunPage           = lazy(() => import("./pages/bugun").then(m => ({ default: m.BugunPage })));
const TakvimPage          = lazy(() => import("./pages/takvim").then(m => ({ default: m.TakvimPage })));
const ProgramlarPage      = lazy(() => import("./pages/programlar").then(m => ({ default: m.ProgramlarPage })));
const EgitmenlerOzetPage  = lazy(() => import("./pages/egitmenler").then(m => ({ default: m.EgitmenlerOzetPage })));
const KisiDosyasiPage     = lazy(() => import("./pages/kisiler/detail").then(m => ({ default: m.KisiDosyasiPage })));

/**
 * Sekme başlığı: menüdeki sayfa adı ("Kişiler · Edel Yönetim"). Bölümün ilk
 * sekmesi ("Genel bakış", "Genel" gibi) tek başına anlamsız → bölüm adı.
 */
function pageTitle({ pathname, autoGeneratedTitle }: { pathname?: string; autoGeneratedTitle: string }) {
  const hit = findNav(pathname ?? window.location.pathname);
  if (hit) return `${hit.tab === hit.section.tabs[0] || !hit.tab.label ? hit.section.label : hit.tab.label} · Edel Yönetim`;
  if ((pathname ?? window.location.pathname).startsWith("/login")) return "Giriş · Edel Yönetim";
  const t = autoGeneratedTitle.replace(/\s*\|\s*Refine$/, "").trim();
  return !t || t === "Refine" ? "Edel Yönetim" : `${t} · Edel Yönetim`;
}

/** Yeni kabukta açılış ekranı yok: /hub → Bugün. Eski kabukta launcher. */
function HubRoute() {
  const v2 = useShellV2();
  return v2 ? <Navigate to="/" replace /> : <HubPage />;
}

/** RBAC route guard (FAZ D): scope'u yetmeyen alan URL'den açılırsa panele yönlendirir. */
function ScopeGuard() {
  const { data: perms } = usePermissions<{ scopes?: string[] } | null>({});
  const location = useLocation();
  const scopes = perms?.scopes ?? null;
  if (!canSeeArea(areaForPath(location.pathname), scopes)) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

/**
 * Refine kbar yalnız eski kabukta kurulur. Yeni kabukta ⌘K kendi paletimize
 * ait; kbar sağlayıcısı kurulursa aynı kısayolu dinleyip arama kutusu olmadan
 * açılmaya çalışır ve hata fırlatır (RefineKbarProvider seçenek iletmiyor).
 */
function KbarGate({ children }: { children: ReactNode }) {
  const shellV2 = useShellV2();
  return shellV2 ? <>{children}</> : <RefineKbarProvider>{children}</RefineKbarProvider>;
}

function App() {
  return (
    <BrowserRouter>
      <KbarGate>
        <ColorModeContextProvider>
          <AntdApp>
            <DevtoolsProvider>
              <Refine
                dataProvider={dataProvider}
                liveProvider={liveProvider(supabaseClient)}
                authProvider={authProvider}
                routerProvider={routerProvider}
                notificationProvider={useNotificationProvider}
                resources={[
                  // ── Gösterge Paneli ────────────────────────
                  {
                    name: "dashboard",
                    list: "/",
                    meta: {
                      label: "Gösterge Paneli",
                      icon: <DashboardOutlined />,
                    },
                  },

                  // ── Satış & CRM ────────────────────────────
                  {
                    name: "crm",
                    meta: { label: "Satış & CRM", icon: <RiseOutlined /> },
                  },
                  {
                    name: "leads",
                    list: "/leads",
                    meta: {
                      label: "Müşteri Adayları",
                      parent: "crm",
                      icon: <FunnelPlotOutlined />,
                    },
                  },
                  {
                    name: "coupons",
                    list: "/coupons",
                    meta: { label: "Kuponlar", parent: "crm", icon: <GiftOutlined /> },
                  },

                  // ── Eğitim & Dersler ───────────────────────
                  {
                    name: "egitim",
                    meta: { label: "Eğitim & Dersler", icon: <ReadOutlined /> },
                  },
                  {
                    name: "appointments",
                    list: "/appointments",
                    meta: {
                      label: "Randevular",
                      parent: "egitim",
                      icon: <CalendarOutlined />,
                    },
                  },
                  {
                    name: "groups",
                    list: "/groups",
                    meta: {
                      label: "Grup Dersleri",
                      parent: "egitim",
                      icon: <UsergroupAddOutlined />,
                    },
                  },
                  {
                    name: "speaking-club",
                    list: "/speaking-club",
                    meta: {
                      label: "Speaking Club",
                      parent: "egitim",
                      icon: <AudioOutlined />,
                    },
                  },
                  {
                    name: "coaching",
                    list: "/coaching",
                    meta: {
                      label: "Eğitim Koçluğu",
                      parent: "egitim",
                      icon: <TeamOutlined />,
                    },
                  },
                  {
                    name: "demo",
                    list: "/demo",
                    meta: {
                      label: "Demo Yönetimi",
                      parent: "egitim",
                      icon: <ExperimentOutlined />,
                    },
                  },
                  {
                    name: "courses",
                    list: "/courses",
                    meta: { label: "Kurslar", parent: "egitim", icon: <BookOutlined /> },
                  },
                  {
                    name: "materials",
                    list: "/kutuphane",
                    meta: {
                      label: "Kütüphane",
                      parent: "egitim",
                      icon: <FileOutlined />,
                    },
                  },
                  {
                    name: "homework",
                    list: "/odev",
                    meta: {
                      label: "Ödevler",
                      parent: "egitim",
                      icon: <FileTextOutlined />,
                    },
                  },

                  // ── Öğrenciler ─────────────────────────────
                  {
                    name: "ogrenciler",
                    meta: { label: "Öğrenciler", icon: <UserOutlined /> },
                  },
                  {
                    name: "students",
                    list: "/ogrenciler",
                    meta: {
                      label: "Öğrenci Listesi",
                      parent: "ogrenciler",
                      icon: <TeamOutlined />,
                    },
                  },
                  {
                    name: "credits",
                    list: "/credits",
                    meta: {
                      label: "Ders Hakkı Yönetimi",
                      parent: "ogrenciler",
                      icon: <CreditCardOutlined />,
                    },
                  },
                  {
                    name: "placements",
                    list: "/placements",
                    meta: {
                      label: "Seviye Tespit",
                      parent: "ogrenciler",
                      icon: <ExperimentOutlined />,
                    },
                  },
                  {
                    name: "reviews",
                    list: "/reviews",
                    meta: {
                      label: "Değerlendirmeler",
                      parent: "ogrenciler",
                      icon: <StarOutlined />,
                    },
                  },

                  // ── Eğitmenler ─────────────────────────────
                  {
                    name: "egitmenler",
                    meta: {
                      label: "Eğitmenler",
                      icon: <SafetyCertificateOutlined />,
                    },
                  },
                  {
                    name: "teachers",
                    list: "/egitmenler",
                    meta: {
                      label: "Eğitmen Listesi",
                      parent: "egitmenler",
                      icon: <TeamOutlined />,
                    },
                  },
                  {
                    name: "teacher-applications",
                    list: "/teacher-applications",
                    meta: {
                      label: "Başvurular",
                      parent: "egitmenler",
                      icon: <SafetyCertificateOutlined />,
                    },
                  },
                  {
                    name: "availability",
                    list: "/availability",
                    meta: {
                      label: "Müsaitlik",
                      parent: "egitmenler",
                      icon: <ScheduleOutlined />,
                    },
                  },

                  // ── Finans (Maliye + Ödemeler + Raporlar) ──
                  {
                    name: "finans",
                    meta: { label: "Finans", icon: <AccountBookOutlined /> },
                  },
                  {
                    name: "maliye-overview",
                    list: "/finans",
                    meta: {
                      label: "Genel Bakış",
                      parent: "finans",
                      icon: <FundOutlined />,
                    },
                  },
                  {
                    name: "sales",
                    list: "/finans/tahsilat",
                    meta: {
                      label: "Tahsilatlar",
                      parent: "finans",
                      icon: <WalletOutlined />,
                    },
                  },
                  {
                    name: "maliye-transactions",
                    list: "/maliye/transactions",
                    meta: {
                      label: "Gelir & Gider",
                      parent: "finans",
                      icon: <SwapOutlined />,
                    },
                  },
                  {
                    name: "maliye-accounts",
                    list: "/maliye/accounts",
                    meta: {
                      label: "Kasa & Hesaplar",
                      parent: "finans",
                      icon: <WalletOutlined />,
                    },
                  },
                  {
                    name: "maliye-payables",
                    list: "/maliye/payables",
                    meta: {
                      label: "Alacak & Borç",
                      parent: "finans",
                      icon: <ReconciliationOutlined />,
                    },
                  },
                  {
                    name: "maliye-recurring",
                    list: "/maliye/recurring",
                    meta: {
                      label: "Tekrarlayan",
                      parent: "finans",
                      icon: <RetweetOutlined />,
                    },
                  },
                  {
                    name: "maliye-budgets",
                    list: "/maliye/budgets",
                    meta: {
                      label: "Bütçe",
                      parent: "finans",
                      icon: <PieChartOutlined />,
                    },
                  },
                  {
                    name: "cash-accounts",
                    list: "/finans/kasa",
                    meta: { label: "Kasa & Hesaplar", parent: "finans", icon: <WalletOutlined /> },
                  },
                  {
                    name: "expenses",
                    list: "/finans/gider",
                    meta: { label: "Giderler", parent: "finans", icon: <SwapOutlined /> },
                  },
                  {
                    name: "teacher-payouts",
                    list: "/finans/egitmen",
                    meta: {
                      label: "Eğitmen Ödemeleri",
                      parent: "finans",
                      icon: <BankOutlined />,
                    },
                  },
                  {
                    name: "teacher-finance",
                    list: "/finance",
                    meta: {
                      label: "Eğitmen Cüzdanları (eski)",
                      parent: "finans",
                      icon: <BankOutlined />,
                    },
                  },
                  {
                    name: "maliye-reports",
                    list: "/maliye/reports",
                    meta: {
                      label: "Raporlar",
                      parent: "finans",
                      icon: <BarChartOutlined />,
                    },
                  },

                  // ── İletişim ───────────────────────────────
                  {
                    name: "iletisim",
                    meta: { label: "İletişim", icon: <SoundOutlined /> },
                  },
                  {
                    name: "announcements",
                    list: "/announcements",
                    meta: {
                      label: "Duyurular",
                      parent: "iletisim",
                      icon: <SoundOutlined />,
                    },
                  },
                  {
                    name: "notifications",
                    list: "/notifications",
                    meta: {
                      label: "Bildirimler",
                      parent: "iletisim",
                      icon: <BellOutlined />,
                    },
                  },
                  {
                    name: "messages",
                    list: "/messages",
                    meta: {
                      label: "Mesajlar",
                      parent: "iletisim",
                      icon: <MessageOutlined />,
                    },
                  },
                  {
                    name: "surveys",
                    list: "/geri-bildirim",
                    meta: {
                      label: "Geri bildirim",
                      parent: "iletisim",
                      icon: <FileDoneOutlined />,
                    },
                  },
                  {
                    name: "marketing_campaigns",
                    list: "/campaigns",
                    meta: {
                      label: "Kampanyalar",
                      parent: "iletisim",
                      icon: <NotificationOutlined />,
                    },
                  },

                  // ── Sistem ─────────────────────────────────
                  {
                    name: "sistem",
                    meta: { label: "Sistem", icon: <ToolOutlined /> },
                  },
                  {
                    name: "deleted-accounts",
                    list: "/deleted-accounts",
                    meta: {
                      label: "Silinen Hesaplar",
                      parent: "sistem",
                      icon: <UserDeleteOutlined />,
                    },
                  },
                  {
                    name: "audit",
                    list: "/ayarlar/kayitlar",
                    meta: {
                      label: "İşlem Geçmişi",
                      parent: "sistem",
                      icon: <HistoryOutlined />,
                    },
                  },
                  {
                    name: "settings",
                    list: "/ayarlar",
                    meta: {
                      label: "Ayarlar",
                      parent: "sistem",
                      icon: <SettingOutlined />,
                    },
                  },
                  {
                    name: "notification-settings",
                    list: "/ayarlar/ekip",
                    meta: {
                      label: "E-posta Bildirimleri",
                      parent: "sistem",
                      icon: <MailOutlined />,
                    },
                  },

                  // ══ Planlama çalışma alanı ═════════════════
                  {
                    name: "planlama",
                    meta: { label: "Planlama", icon: <ProjectOutlined /> },
                  },
                  {
                    name: "planlama-tasks",
                    list: "/gorevler",
                    meta: {
                      label: "Görevler",
                      parent: "planlama",
                      icon: <CheckSquareOutlined />,
                    },
                  },
                  {
                    name: "planlama-board",
                    list: "/gorevler/pano",
                    meta: {
                      label: "Görev Panosu",
                      parent: "planlama",
                      icon: <AppstoreOutlined />,
                    },
                  },
                  {
                    name: "planlama-notes",
                    list: "/gorevler/toplantilar",
                    meta: {
                      label: "Toplantılar",
                      parent: "planlama",
                      icon: <FileTextOutlined />,
                    },
                  },
                  {
                    name: "planlama-calendar",
                    list: "/planlama/takvim",
                    meta: {
                      label: "Takvim",
                      parent: "planlama",
                      icon: <CalendarOutlined />,
                    },
                  },

                  // ══ Dosya Paylaşımı (Drive) ════════════════
                  {
                    name: "drive",
                    meta: {
                      label: "Dosya Paylaşımı",
                      icon: <FolderOpenOutlined />,
                    },
                  },
                  {
                    name: "drive-files",
                    list: "/drive",
                    meta: {
                      label: "Dosyalar",
                      parent: "drive",
                      icon: <FolderOutlined />,
                    },
                  },
                  {
                    name: "drive-favorites",
                    list: "/drive/favoriler",
                    meta: {
                      label: "Favoriler",
                      parent: "drive",
                      icon: <StarOutlined />,
                    },
                  },
                  {
                    name: "drive-activity",
                    list: "/drive/aktivite",
                    meta: {
                      label: "Aktivite",
                      parent: "drive",
                      icon: <HistoryOutlined />,
                    },
                  },

                  // ══ Büyüme çalışma alanı ═══════════════════
                  {
                    name: "buyume",
                    meta: { label: "Büyüme", icon: <ThunderboltOutlined /> },
                  },
                  {
                    name: "buyume-inbox",
                    list: "/buyume",
                    meta: {
                      label: "Gelen Kutusu",
                      parent: "buyume",
                      icon: <WhatsAppOutlined />,
                    },
                  },
                  {
                    name: "buyume-ads",
                    list: "/buyume/reklamlar",
                    meta: {
                      label: "Reklam Merkezi",
                      parent: "buyume",
                      icon: <FundOutlined />,
                    },
                  },
                  {
                    name: "buyume-channels",
                    list: "/buyume/kanallar",
                    meta: {
                      label: "Kanallar & Kurulum",
                      parent: "buyume",
                      icon: <ApiOutlined />,
                    },
                  },
                  {
                    name: "buyume-ai",
                    list: "/buyume/ai",
                    meta: {
                      label: "AI Closer",
                      parent: "buyume",
                      icon: <RobotOutlined />,
                    },
                  },
                  {
                    name: "buyume-content",
                    list: "/buyume/icerik",
                    meta: {
                      label: "İçerik Stüdyosu",
                      parent: "buyume",
                      icon: <BulbOutlined />,
                    },
                  },
                  {
                    name: "buyume-reactivation",
                    list: "/buyume/reaktivasyon",
                    meta: {
                      label: "Reaktivasyon",
                      parent: "buyume",
                      icon: <RetweetOutlined />,
                    },
                  },
                ]}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  projectId: "vUYAOA-E7tfPv-Zb97wm",
                }}
              >
                <Routes>
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-inner"
                        fallback={<CatchAllNavigate to="/login" />}
                      >
                        <NavProvider>
                          <ShellLayout>
                            <Suspense
                              fallback={
                                <div style={{ display: "flex", justifyContent: "center", padding: 64 }}>
                                  <Spin size="large" />
                                </div>
                              }
                            >
                              <ScopeGuard />
                            </Suspense>
                          </ShellLayout>
                        </NavProvider>
                      </Authenticated>
                    }
                  >
                    {/* Faz 3: açılış = Bugün (kişiye özel iş listesi); eski grafikler /gosterge */}
                    <Route index element={<BugunPage />} />
                    <Route path="/gosterge" element={<DashboardPage />} />
                    {/* Faz 4: birleşik takvim */}
                    <Route path="/takvim" element={<TakvimPage />} />
                    {/* Faz 5: programlar özeti */}
                    <Route path="/programlar" element={<ProgramlarPage />} />
                    <Route path="/egitmenler/ozet" element={<EgitmenlerOzetPage />} />
                    {/* ── Kişiler (Faz 2): tek yolculuk ── */}
                    <Route path="/kisiler" element={<KisilerPage />} />
                    <Route path="/kisiler/:id" element={<KisiDosyasiPage />} />
                    <Route path="/leads" element={<LeadsPage />} />
                    <Route path="/coupons" element={<CouponsPage />} />
                    <Route
                      path="/appointments"
                      element={<AppointmentsPage />}
                    />
                    <Route path="/groups" element={<GroupsPage />} />
                    {/* Eski /cohorts deep link'i yönlendir — UX artık tek sayfa içinde */}
                    <Route path="/cohorts" element={<Navigate to="/groups" replace />} />
                    <Route
                      path="/speaking-club"
                      element={<SpeakingClubPage />}
                    />
                    <Route path="/coaching" element={<CoachingPage />} />
                    <Route path="/demo" element={<DemoPage />} />
                    <Route path="/courses" element={<CoursesPage />} />
                    <Route path="/kutuphane" element={<KutuphanePage />} />
                    <Route path="/materials" element={<Navigate to="/kutuphane" replace />} />
                    <Route path="/odev" element={<OdevOzetPage />} />
                    <Route path="/odev/liste" element={<OdevListePage />} />
                    <Route path="/odev/planlar" element={<PlanlarPage />} />
                    <Route path="/homework" element={<Navigate to="/odev" replace />} />
                    <Route path="/availability" element={<AvailabilityPage />} />
                    <Route path="/placements" element={<PlacementsPage />} />
                    <Route path="/credits" element={<CreditsPage />} />
                    {/* F7.4a: eski eğitmen cüzdanı → Eğitmen ödemeleri (ödeme talepleri dahil) */}
                    <Route path="/finance" element={<Navigate to="/finans/egitmen" replace />} />
                    {/* Eski genel /reports → Maliye Raporlar'a birleştirildi */}
                    <Route path="/reports" element={<Navigate to="/maliye/reports" replace />} />
                    <Route path="/finans" element={<FinansGenelPage />} />
                    <Route path="/maliye" element={<Navigate to="/finans" replace />} />
                    <Route path="/finans/tahsilat" element={<TahsilatPage />} />
                    <Route path="/finans/tahsilat/eslestir" element={<EslestirPage />} />
                    <Route path="/finans/egitmen" element={<EgitmenOdemeleriPage />} />
                    <Route path="/finans/kasa" element={<KasaPage />} />
                    <Route path="/finans/gider" element={<GiderPage />} />
                    <Route path="/maliye/transactions" element={<MaliyeTransactionsPage />} />
                    <Route path="/maliye/accounts" element={<MaliyeAccountsPage />} />
                    <Route path="/maliye/payables" element={<MaliyePayablesPage />} />
                    <Route path="/maliye/recurring" element={<MaliyeRecurringPage />} />
                    <Route path="/maliye/budgets" element={<MaliyeBudgetsPage />} />
                    <Route path="/maliye/reports" element={<MaliyeReportsPage />} />
                    <Route path="/profiles" element={<ProfilesPage />} />
                    <Route path="/ogrenciler" element={<ProfilesPage initialRole="student" />} />
                    <Route path="/egitmenler" element={<ProfilesPage initialRole="teacher" />} />
                    <Route path="/reviews" element={<ReviewsPage />} />
                    <Route
                      path="/announcements"
                      element={<AnnouncementsPage />}
                    />
                    <Route
                      path="/notifications"
                      element={<NotificationsPage />}
                    />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/geri-bildirim" element={<GeriBildirimPage />} />
                    <Route path="/anket" element={<Navigate to="/geri-bildirim" replace />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    {/* F7.3: Ayarlar — Genel · Ekip ve erişim · Hesaplar (/profiles) · Kayıtlar; eski adresler yönlenir */}
                    <Route path="/ayarlar" element={<SettingsPage />} />
                    <Route path="/ayarlar/ekip" element={<TeamAccessPage />} />
                    <Route path="/ayarlar/kayitlar" element={<RecordsPage />} />
                    <Route path="/settings" element={<Navigate to="/ayarlar" replace />} />
                    <Route path="/notification-settings" element={<Navigate to="/ayarlar/ekip#bildirim" replace />} />
                    <Route path="/audit" element={<Navigate to="/ayarlar/kayitlar" replace />} />
                    <Route path="/deleted-accounts" element={<Navigate to="/ayarlar/kayitlar?bolum=silinen" replace />} />
                    <Route path="/teacher-applications" element={<TeacherApplicationsPage />} />
                    {/* ── Planlama çalışma alanı ── */}
                    {/* F7.1: Görevler & Toplantılar — /gorevler; eski /planlama adresleri yönlenir */}
                    <Route path="/gorevler" element={<GorevlerPage />} />
                    <Route path="/gorevler/pano" element={<PlanlamaBoardPage />} />
                    <Route path="/gorevler/toplantilar" element={<MeetingsPage />} />
                    <Route path="/gorevler/toplantilar/yeni" element={<MeetingPage />} />
                    <Route path="/gorevler/toplantilar/:id" element={<MeetingDetailPage />} />
                    <Route path="/planlama" element={<Navigate to="/gorevler" replace />} />
                    <Route path="/planlama/notlar" element={<Navigate to="/gorevler/toplantilar" replace />} />
                    <Route path="/planlama/yapilacaklar" element={<Navigate to="/gorevler" replace />} />
                    {/* F7.4a: ekip takvimi → Takvim "Ekip" katmanı */}
                    <Route path="/planlama/takvim" element={<Navigate to="/takvim" replace />} />
                    {/* F7.2: günlük rapor yerine haftalık not (toplantının ilk adımında) */}
                    <Route path="/planlama/raporlar" element={<Navigate to="/gorevler/toplantilar" replace />} />
                    {/* ── Dosya Paylaşımı (Drive) ── */}
                    <Route path="/drive" element={<DrivePage />} />
                    <Route path="/drive/favoriler" element={<DriveFavoritesPage />} />
                    <Route path="/drive/aktivite" element={<DriveActivityPage />} />
                    {/* ── Büyüme çalışma alanı ── */}
                    <Route path="/buyume" element={<BuyumeInboxPage />} />
                    <Route path="/buyume/pipeline" element={<Navigate to="/leads" replace />} />
                    <Route path="/buyume/reklamlar" element={<BuyumeAdsPage />} />
                    <Route path="/buyume/kanallar" element={<BuyumeChannelsPage />} />
                    <Route path="/buyume/ai" element={<BuyumeAiPage />} />
                    <Route path="/buyume/icerik" element={<BuyumeContentPage />} />
                    <Route path="/buyume/reaktivasyon" element={<BuyumeReactivationPage />} />
                    {/* ── Labs ── */}
                    <Route path="/labs/ui" element={<UiShowcasePage />} />
                  </Route>
                  {/* Launcher — CRM sider'ı olmadan tam ekran */}
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-hub"
                        fallback={<CatchAllNavigate to="/login" />}
                      >
                        <Suspense
                          fallback={
                            <div style={{ display: "flex", justifyContent: "center", padding: 64 }}>
                              <Spin size="large" />
                            </div>
                          }
                        >
                          <Outlet />
                        </Suspense>
                      </Authenticated>
                    }
                  >
                    <Route path="/hub" element={<HubRoute />} />
                  </Route>
                  {import.meta.env.DEV && (
                    <Route
                      element={
                        <NavProvider>
                          <ShellLayout>
                            <Suspense fallback={<Spin />}>
                              <Outlet />
                            </Suspense>
                          </ShellLayout>
                        </NavProvider>
                      }
                    >
                      {/* Yalnız geliştirme: oturumsuz görsel kontrol (veri RLS ile boş gelir) */}
                      <Route path="/__ui" element={<UiShowcasePage />} />
                      <Route path="/__ui/kisiler" element={<KisilerPage />} />
                      <Route path="/__ui/kisiler/:id" element={<KisiDosyasiPage />} />
                      <Route path="/__ui/bugun" element={<BugunPage />} />
                      <Route path="/__ui/takvim" element={<TakvimPage />} />
                      <Route path="/__ui/kutuphane" element={<KutuphanePage />} />
                      <Route path="/__ui/odev" element={<OdevOzetPage />} />
                      <Route path="/__ui/odev/liste" element={<OdevListePage />} />
                      <Route path="/__ui/odev/planlar" element={<PlanlarPage />} />
                      <Route path="/__ui/geri-bildirim" element={<GeriBildirimPage />} />
                      <Route path="/__ui/finans/tahsilat" element={<TahsilatPage />} />
                      <Route path="/__ui/finans/eslestir" element={<EslestirPage />} />
                      <Route path="/__ui/finans/egitmen" element={<EgitmenOdemeleriPage />} />
                      <Route path="/__ui/finans/kasa" element={<KasaPage />} />
                      <Route path="/__ui/finans" element={<FinansGenelPage />} />
                      <Route path="/__ui/finans/gider" element={<GiderPage />} />
                    </Route>
                  )}
                  <Route
                    element={
                      <Authenticated
                        key="authenticated-outer"
                        fallback={<Outlet />}
                      >
                        <Navigate to="/hub" replace />
                      </Authenticated>
                    }
                  >
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  </Route>
                </Routes>
                <LegacyKbar />
                <UnsavedChangesNotifier />
                <DocumentTitleHandler handler={pageTitle} />
              </Refine>
              {import.meta.env.DEV && <DevtoolsPanel />}
            </DevtoolsProvider>
          </AntdApp>
        </ColorModeContextProvider>
      </KbarGate>
    </BrowserRouter>
  );
}

export default App;
